import { FC, useMemo } from 'react'
import { useTranslation } from 'react-i18next'

import { Box } from '@mui/material'

import { GRTable, GRTableColumn } from '../../../../components/GRTable/GRTable'
import { creativeTagListingColumns } from '../../helpers/tagColumns'
import { useControlledColumns } from '../../hooks/useControlledColumns'
import { useDimensionTagsListing } from '../../hooks/useDimensionTagsListing'
import { useUAInsightsNavigation } from '../../hooks/useUAInsightsNavigation'
import { useUATaxonomy } from '../../hooks/useUATaxonomy'
import { CreativeTag } from '../../types'
import { ListingTruncationNote } from '../ListingTruncationNote/ListingTruncationNote'
import { SectionCard } from '../SectionCard/SectionCard'
import { TableSkeleton } from '../TableSkeleton/TableSkeleton'
import { UAInsightsDialog } from '../UAInsightsDialog/UAInsightsDialog'

const tagRowId = (row: CreativeTag) => row.tag.id

/**
 * Dialog that lists every creative tag belonging to a single dimension (aka
 * "group"), e.g. every tag under "Setting". Shares `creativeTagListingColumns`
 * with `GenreTagsListingDialog` so the drill-down experience stays consistent
 * across entry points.
 */
export const DimensionTagsListingDialog: FC = () => {
  const { t } = useTranslation('ua-insights')
  const { params, close, canGoBack, goBack, openTag, openMotivation, openTagGames, openGenreTags } = useUAInsightsNavigation()
  const taxonomy = useUATaxonomy()
  const open = params.dialog === 'dimension-tags'
  const dimensionId = open ? params.dimensionId : null
  const { data, isLoading, isError, refetch } = useDimensionTagsListing(dimensionId)

  const defaultColumns: GRTableColumn<CreativeTag, undefined>[] = useMemo(
    () =>
      creativeTagListingColumns(t, taxonomy, {
        onTagClick: openTag,
        onTagGamesClick: openTagGames,
        onGenreClick: openGenreTags,
        onMotivationClick: openMotivation,
      }),
    [t, taxonomy, openTag, openTagGames, openGenreTags, openMotivation]
  )

  const [columns, setColumns] = useControlledColumns(defaultColumns)

  const title = dimensionId ? t('all_dimension_creative_tags', { dimension: taxonomy.dimensionLabel(dimensionId) }) : t('full_tag_list')

  return (
    <UAInsightsDialog
      open={open}
      onClose={close}
      title={title}
      canGoBack={canGoBack}
      onGoBack={goBack}
      showNetworkSelector
      hasData={!!data}
      isLoading={isLoading}
      isError={isError}
      onRetry={refetch}
      renderChildrenWhenEmpty
      loadingPlaceholder={<TableSkeleton rows={10} columns={5} />}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
        <SectionCard title={title} disableBodyPadding>
          <GRTable rows={data?.tags ?? []} columns={columns} onColumnsUpdated={setColumns} striped hoverable rowIdKey={tagRowId} noRowsLabel={t('no_tags')} />
          <ListingTruncationNote shown={data?.tags.length ?? 0} total={data?.total} />
        </SectionCard>
      </Box>
    </UAInsightsDialog>
  )
}
